export interface City {
  name: string
  slug: string
}

export interface Country {
  name: string
  slug: string
  region: string
  summary: string
  keyFacts: string[]
  cities: City[]
}

export const countries: Country[] = [
  // ── Europe ──────────────────────────────────────────────────────────────────
  {
    name: 'United Kingdom',
    slug: 'united-kingdom',
    region: 'Europe',
    summary:
      'The United Kingdom has one of the richest poker cultures in the world, shaped by a long tradition of card gaming and a lively casino scene stretching from London to Glasgow. British players have long punched above their weight on the international tournament circuit, and the country remains a major destination for poker enthusiasts of every level.',
    keyFacts: [
      'London is home to dozens of active poker rooms, including the Hippodrome Casino and multiple Grosvenor venues',
      'UK players have earned World Series of Poker bracelets and made deep runs at the biggest global tournaments',
      'The British poker scene spans recreational home games, dedicated casino regulars, and full-time professionals',
      'Major European poker tours regularly include UK stops that draw international fields',
      'Online poker is one of the most popular competitive card games played across England, Scotland, Wales, and Northern Ireland',
    ],
    cities: [
      { name: 'London', slug: 'london' },
      { name: 'Manchester', slug: 'manchester' },
      { name: 'Birmingham', slug: 'birmingham' },
      { name: 'Edinburgh', slug: 'edinburgh' },
      { name: 'Glasgow', slug: 'glasgow' },
    ],
  },
  {
    name: 'Germany',
    slug: 'germany',
    region: 'Europe',
    summary:
      'Germany has a deep tradition of card games and a passionate poker community spread across its major cities. The country has produced strong tournament players and hosts a steady calendar of live events, while online poker remains a popular pastime for millions of recreational and serious players.',
    keyFacts: [
      'German players regularly cash at major European and international tournaments',
      'Berlin and Munich have well-established poker room scenes that attract players from across Central Europe',
      'Germany hosts stops on major European poker tours throughout the year',
      "The country's strong gaming infrastructure supports a large and active poker community",
      'Online poker is widely enjoyed at all skill levels, from casual weekend players to dedicated grinders',
    ],
    cities: [
      { name: 'Berlin', slug: 'berlin' },
      { name: 'Hamburg', slug: 'hamburg' },
      { name: 'Munich', slug: 'munich' },
      { name: 'Frankfurt', slug: 'frankfurt' },
      { name: 'Cologne', slug: 'cologne' },
    ],
  },
  {
    name: 'France',
    slug: 'france',
    region: 'Europe',
    summary:
      "France has a storied poker tradition, with a sophisticated player base that spans from Parisian casino regulars to world-class tournament competitors. French players are consistently represented at the highest levels of the game, and the country's poker culture blends competitive drive with a distinctly European flair.",
    keyFacts: [
      'French players regularly reach final tables at the World Series of Poker and European Poker Tour',
      "Paris has historically been one of Europe's great poker cities, home to storied casino venues",
      'France hosts major European tournament stops that draw fields from across the continent',
      'Online poker is popular with a broad cross-section of French society',
      'France has contributed significantly to global poker strategy and theory through its community of thinking players',
    ],
    cities: [
      { name: 'Paris', slug: 'paris' },
      { name: 'Lyon', slug: 'lyon' },
      { name: 'Marseille', slug: 'marseille' },
      { name: 'Toulouse', slug: 'toulouse' },
      { name: 'Nice', slug: 'nice' },
    ],
  },
  {
    name: 'Italy',
    slug: 'italy',
    region: 'Europe',
    summary:
      "Italy has one of the most enthusiastic poker communities in Southern Europe, with a player base that ranges from casual home game enthusiasts to professional tournament specialists. Italian players have made their mark on the global stage, and the country's casino scene anchors a thriving live poker culture.",
    keyFacts: [
      'Italian players have won World Series of Poker bracelets and made major international tournament cashes',
      "Casino di Venezia and Casino di Campione host some of Italy's most prestigious poker events",
      'The Italian poker community is active online and at major European live festivals',
      'Home games and casino poker nights are deeply embedded in Italian social culture',
      'Major European tours regularly schedule Italian stops drawing large and competitive fields',
    ],
    cities: [
      { name: 'Rome', slug: 'rome' },
      { name: 'Milan', slug: 'milan' },
      { name: 'Naples', slug: 'naples' },
      { name: 'Turin', slug: 'turin' },
      { name: 'Florence', slug: 'florence' },
    ],
  },
  {
    name: 'Spain',
    slug: 'spain',
    region: 'Europe',
    summary:
      "Spain has a vibrant poker scene centered on Barcelona, which has hosted some of the largest and most celebrated poker festivals in Europe. The country's warm culture and passion for competition have helped build an enthusiastic player base that spans all skill levels.",
    keyFacts: [
      "Barcelona is one of Europe's premier poker destinations, hosting major international festivals",
      'Spanish players are regulars at major European and World Series of Poker events',
      'Casino Barcelona is one of the most famous poker venues in continental Europe',
      'Online poker has a large and growing following among Spanish recreational players',
      "The country's coastal cities host a thriving live tournament circuit throughout the year",
    ],
    cities: [
      { name: 'Madrid', slug: 'madrid' },
      { name: 'Barcelona', slug: 'barcelona' },
      { name: 'Valencia', slug: 'valencia' },
      { name: 'Seville', slug: 'seville' },
      { name: 'Bilbao', slug: 'bilbao' },
    ],
  },
  {
    name: 'Netherlands',
    slug: 'netherlands',
    region: 'Europe',
    summary:
      'The Netherlands has produced some of the most successful poker players in the world and has a uniquely strategic approach to the game rooted in its tradition of analytical thinking and competition. Amsterdam and other Dutch cities host an active live poker scene alongside a popular online community.',
    keyFacts: [
      'Dutch players have won World Series of Poker Main Events and numerous other major titles',
      'Holland Casino operates venues across the country with a well-established poker program',
      'The Netherlands has a disproportionately high number of professional and semi-professional poker players for its population size',
      'Online poker is extremely popular, with Dutch players active on international platforms',
      'Dutch poker players are known for their mathematical and game-theory-based approaches',
    ],
    cities: [
      { name: 'Amsterdam', slug: 'amsterdam' },
      { name: 'Rotterdam', slug: 'rotterdam' },
      { name: 'The Hague', slug: 'the-hague' },
      { name: 'Utrecht', slug: 'utrecht' },
      { name: 'Eindhoven', slug: 'eindhoven' },
    ],
  },
  {
    name: 'Sweden',
    slug: 'sweden',
    region: 'Europe',
    summary:
      'Sweden is one of the most prolific countries in the world for producing elite poker talent, with a disproportionately high number of world-class players relative to its population. The Swedish poker boom of the early 2000s launched careers that are still active at the top of the game today.',
    keyFacts: [
      'Swedish players have won World Series of Poker Main Events and dozens of major international titles',
      "Sweden's online poker community played a foundational role in the growth of internet poker",
      'Stockholm and Gothenburg have active casino poker scenes alongside robust online play',
      'Swedish players are particularly known for aggressive and mathematically sound strategies',
      'The Swedish poker community produces a steady stream of high-stakes players and coaches',
    ],
    cities: [
      { name: 'Stockholm', slug: 'stockholm' },
      { name: 'Gothenburg', slug: 'gothenburg' },
      { name: 'Malmö', slug: 'malmo' },
      { name: 'Uppsala', slug: 'uppsala' },
      { name: 'Västerås', slug: 'vasteras' },
    ],
  },
  {
    name: 'Denmark',
    slug: 'denmark',
    region: 'Europe',
    summary:
      "Denmark has a surprisingly strong poker scene for a country of its size, with Copenhagen's casino circuit and a nationally ranked online player base that regularly competes at major international events. Danish players are known for disciplined, thoughtful play.",
    keyFacts: [
      'Danish players have made final tables at World Series of Poker and European Poker Tour events',
      'Casino Copenhagen and other Danish venues run regular poker tournaments',
      'Online poker has a substantial following among Danish recreational and serious players',
      'The Danish poker community has produced a number of respected coaches and strategists',
      'Denmark regularly sends players to major European live festival stops',
    ],
    cities: [
      { name: 'Copenhagen', slug: 'copenhagen' },
      { name: 'Aarhus', slug: 'aarhus' },
      { name: 'Odense', slug: 'odense' },
      { name: 'Aalborg', slug: 'aalborg' },
      { name: 'Esbjerg', slug: 'esbjerg' },
    ],
  },
  {
    name: 'Finland',
    slug: 'finland',
    region: 'Europe',
    summary:
      "Finland has a compact but dedicated poker community, with Helsinki's casino scene and a loyal base of online players who compete internationally. Finnish players are known for a methodical approach to the game that mirrors the country's broader culture of careful preparation.",
    keyFacts: [
      'Casino Helsinki is the flagship live poker venue in the country and hosts regular tournament series',
      'Finnish players have cashed at World Series of Poker and European Poker Tour stops',
      'Online poker is one of the most popular skill-based games among Finnish adults',
      'The Finnish community has a culture of study and self-improvement that produces strong players',
      'Nordic poker festivals often include Finnish players among their most active competitors',
    ],
    cities: [
      { name: 'Helsinki', slug: 'helsinki' },
      { name: 'Tampere', slug: 'tampere' },
      { name: 'Turku', slug: 'turku' },
      { name: 'Oulu', slug: 'oulu' },
      { name: 'Jyväskylä', slug: 'jyvaskyla' },
    ],
  },
  {
    name: 'Norway',
    slug: 'norway',
    region: 'Europe',
    summary:
      "Norway has a passionate poker community that has produced players with impressive results on the international stage. Despite the country's relatively small population, Norwegians are regulars at major European and world tournaments, and online poker enjoys wide popularity.",
    keyFacts: [
      'Norwegian players have made World Series of Poker final tables and won major international titles',
      "Oslo's poker rooms draw a competitive and social player base year-round",
      'Online poker is widely played among Norwegian recreational and serious players',
      'The Norwegian poker scene has a strong culture of home games and club-level competition',
      'Norwegian players consistently compete at Nordic and European tournament stops',
    ],
    cities: [
      { name: 'Oslo', slug: 'oslo' },
      { name: 'Bergen', slug: 'bergen' },
      { name: 'Trondheim', slug: 'trondheim' },
      { name: 'Stavanger', slug: 'stavanger' },
      { name: 'Tromsø', slug: 'tromso' },
    ],
  },
  {
    name: 'Austria',
    slug: 'austria',
    region: 'Europe',
    summary:
      "Austria has a long gambling tradition anchored by Casinos Austria, one of Europe's oldest and most recognized casino brands. Vienna's casino scene is world-class, and the country regularly hosts international poker events that attract players from across Central Europe.",
    keyFacts: [
      'Casinos Austria operates premium venues including Casino Vienna, one of the finest in Europe',
      'Austrian players compete regularly at European Poker Tour events and major international festivals',
      'Vienna has hosted prestigious poker tournaments and festivals over many years',
      'Online poker is popular with Austrian players who often compete on international platforms',
      'The Austrian casino infrastructure supports both live and online poker communities',
    ],
    cities: [
      { name: 'Vienna', slug: 'vienna' },
      { name: 'Graz', slug: 'graz' },
      { name: 'Linz', slug: 'linz' },
      { name: 'Salzburg', slug: 'salzburg' },
      { name: 'Innsbruck', slug: 'innsbruck' },
    ],
  },
  {
    name: 'Belgium',
    slug: 'belgium',
    region: 'Europe',
    summary:
      "Belgium has a thriving poker community and has produced some of the most successful tournament players in Europe. The country's central location makes it a hub for cross-border poker culture, and Belgian players are regulars at the biggest European and world events.",
    keyFacts: [
      'Belgian players have won major international poker titles including World Series of Poker events',
      "Casino de Namur and Circus Belgium are among the country's most active poker venues",
      "Belgium's central location in Europe makes it a natural meeting point for the continental poker community",
      'Online poker is broadly popular across the French-speaking and Flemish communities',
      'Belgian players are well represented at European Poker Tour and WPT stops throughout the year',
    ],
    cities: [
      { name: 'Brussels', slug: 'brussels' },
      { name: 'Antwerp', slug: 'antwerp' },
      { name: 'Ghent', slug: 'ghent' },
      { name: 'Liège', slug: 'liege' },
      { name: 'Bruges', slug: 'bruges' },
    ],
  },
  {
    name: 'Czech Republic',
    slug: 'czech-republic',
    region: 'Europe',
    summary:
      "The Czech Republic — and Prague in particular — is one of Europe's most important poker destinations. Prague's iconic casino circuit and affordable tournament structures attract tens of thousands of players from around the world every year, making the city a truly global poker hub.",
    keyFacts: [
      "Prague is home to some of Europe's busiest poker rooms, including King's Casino Rozvadov nearby",
      "The Czech Republic hosts major international festivals that rank among Europe's largest by attendance",
      'Czech players are well represented at European Poker Tour and World Poker Tour stops',
      'Rozvadov on the Czech-German border is one of the highest-volume poker venues on the continent',
      'Online poker has a loyal following among Czech recreational and serious players',
    ],
    cities: [
      { name: 'Prague', slug: 'prague' },
      { name: 'Brno', slug: 'brno' },
      { name: 'Ostrava', slug: 'ostrava' },
      { name: 'Plzeň', slug: 'plzen' },
      { name: 'Rozvadov', slug: 'rozvadov' },
    ],
  },
  {
    name: 'Poland',
    slug: 'poland',
    region: 'Europe',
    summary:
      'Poland has a rapidly growing poker community with a young, enthusiastic player base that is increasingly active on the European and international tournament circuits. Warsaw and Kraków host a steady calendar of live events, and online poker has exploded in popularity in recent years.',
    keyFacts: [
      'Polish players have earned significant cashes at World Series of Poker and European events',
      'Warsaw is home to active casino poker rooms with a growing professional player base',
      'The Polish online poker community has grown substantially and is highly engaged',
      'Poland regularly sends contingents to major European festival stops',
      'Poker is one of the most popular skill-based games played competitively across the country',
    ],
    cities: [
      { name: 'Warsaw', slug: 'warsaw' },
      { name: 'Kraków', slug: 'krakow' },
      { name: 'Wrocław', slug: 'wroclaw' },
      { name: 'Gdańsk', slug: 'gdansk' },
      { name: 'Poznań', slug: 'poznan' },
    ],
  },
  {
    name: 'Portugal',
    slug: 'portugal',
    region: 'Europe',
    summary:
      "Portugal has a charming poker scene centered on Lisbon and the casino strip along the Estoril Coast. The country's casino heritage and the growing international profile of Lisbon as a travel destination have helped build a welcoming and active poker community.",
    keyFacts: [
      "Casino Estoril near Lisbon is one of Europe's oldest and most storied casino venues",
      'Portuguese players are active at European Poker Tour and other major European events',
      'Lisbon has emerged as a popular destination for poker players visiting European festivals',
      'Online poker has a growing and enthusiastic player base across Portugal',
      'The Algarve coast is a popular destination for poker players mixing tourism with live play',
    ],
    cities: [
      { name: 'Lisbon', slug: 'lisbon' },
      { name: 'Porto', slug: 'porto' },
      { name: 'Braga', slug: 'braga' },
      { name: 'Coimbra', slug: 'coimbra' },
      { name: 'Faro', slug: 'faro' },
    ],
  },
  {
    name: 'Romania',
    slug: 'romania',
    region: 'Europe',
    summary:
      "Romania has emerged as one of the most exciting poker nations in Eastern Europe, with a rapidly growing base of talented players who are making names for themselves on the international circuit. Bucharest's casino scene is vibrant and online poker has become enormously popular.",
    keyFacts: [
      'Romanian players have become increasingly visible at World Series of Poker and European major events',
      'Bucharest hosts some of the most active poker rooms in Eastern Europe',
      'The Romanian online poker community is one of the fastest growing in the region',
      'Romania has produced a number of high-volume online professionals who compete internationally',
      'Live tournament action in Romania draws players from across the Balkans and Eastern Europe',
    ],
    cities: [
      { name: 'Bucharest', slug: 'bucharest' },
      { name: 'Cluj-Napoca', slug: 'cluj-napoca' },
      { name: 'Timișoara', slug: 'timisoara' },
      { name: 'Iași', slug: 'iasi' },
      { name: 'Constanța', slug: 'constanta' },
    ],
  },
  {
    name: 'Greece',
    slug: 'greece',
    region: 'Europe',
    summary:
      'Greece has a passionate card-playing culture that extends naturally to poker, with Athens and Thessaloniki hosting active casino rooms and a player base that is enthusiastic about both live and online play. Greek players are regulars at European tournament stops and have made their presence felt globally.',
    keyFacts: [
      "Regency Casino Mont Parnes near Athens is one of Greece's flagship poker venues",
      'Greek players regularly compete at major European and international tournaments',
      'The Greek poker community is social and passionate, with strong home game and club cultures',
      'Online poker has a broad following among Greek players across all skill levels',
      'Greece is a popular destination for poker festivals that combine play with Mediterranean tourism',
    ],
    cities: [
      { name: 'Athens', slug: 'athens' },
      { name: 'Thessaloniki', slug: 'thessaloniki' },
      { name: 'Patras', slug: 'patras' },
      { name: 'Heraklion', slug: 'heraklion' },
      { name: 'Larissa', slug: 'larissa' },
    ],
  },
  {
    name: 'Malta',
    slug: 'malta',
    region: 'Europe',
    summary:
      "Malta punches far above its weight in the global poker and gaming world. The small island nation is the home base of the Malta Poker Festival and serves as the headquarters for many of Europe's major gaming companies, making it a unique intersection of the business and recreational sides of poker.",
    keyFacts: [
      "The Malta Poker Festival is one of Europe's most popular poker events by attendance and prize pool",
      'Malta is the registered home of many major online poker and gaming operators',
      'The Portomaso Casino and Casino Malta host regular live poker action year-round',
      'Players from across Europe flock to Malta for its festival events and favorable environment',
      "Online poker is central to Malta's global gaming industry reputation",
    ],
    cities: [
      { name: 'Valletta', slug: 'valletta' },
      { name: 'Sliema', slug: 'sliema' },
      { name: "St Julian's", slug: 'st-julians' },
      { name: 'Birkirkara', slug: 'birkirkara' },
      { name: 'Msida', slug: 'msida' },
    ],
  },
  {
    name: 'Ireland',
    slug: 'ireland',
    region: 'Europe',
    summary:
      "Ireland has a warm and enthusiastic poker culture, with Dublin's casino rooms and a fiercely loyal online player base that has produced several world-class professionals. The Irish community is known for its sociability and competitive spirit, making for lively games at every level.",
    keyFacts: [
      'Irish players have achieved major results at the World Series of Poker and European Poker Tour',
      "The Fitzwilliam Card Club in Dublin is one of Europe's best-known dedicated poker venues",
      'Ireland hosts the Irish Poker Open, one of the longest-running poker tournaments in Europe',
      'Online poker is widely enjoyed by Irish players, who are active on major international platforms',
      'The Irish poker community is particularly well-known for its camaraderie and sportsmanship',
    ],
    cities: [
      { name: 'Dublin', slug: 'dublin' },
      { name: 'Cork', slug: 'cork' },
      { name: 'Limerick', slug: 'limerick' },
      { name: 'Galway', slug: 'galway' },
      { name: 'Belfast', slug: 'belfast' },
    ],
  },
  {
    name: 'Switzerland',
    slug: 'switzerland',
    region: 'Europe',
    summary:
      "Switzerland's reputation for precision and strategy translates naturally to poker, and the country hosts some of Europe's most refined casino environments. Swiss players are active at major European events, and the country's multilingual culture adds a cosmopolitan flavor to its poker scene.",
    keyFacts: [
      "Grand Casino Baden and Casino Bern are among Switzerland's finest poker venues",
      'Swiss players regularly compete at European Poker Tour and international major events',
      "Zurich and Geneva's poker rooms attract players from across the German and French-speaking regions",
      'Online poker is popular among Swiss players competing on European platforms',
      "Switzerland's geographic center in Europe makes it a natural crossroads for poker tourism",
    ],
    cities: [
      { name: 'Zurich', slug: 'zurich' },
      { name: 'Geneva', slug: 'geneva' },
      { name: 'Basel', slug: 'basel' },
      { name: 'Bern', slug: 'bern' },
      { name: 'Lausanne', slug: 'lausanne' },
    ],
  },
  {
    name: 'Russia',
    slug: 'russia',
    region: 'Europe',
    summary:
      "Russia has produced some of the most prominent poker players in modern history and has a vast community of skilled online players who compete at the highest levels internationally. Moscow and Saint Petersburg have active live scenes, and Russian players are regulars at the world's biggest tournaments.",
    keyFacts: [
      'Russian players have won major World Series of Poker bracelets and international titles',
      'Moscow and Saint Petersburg host regular live poker tournaments attracting large fields',
      'The Russian online poker community is one of the most skilled and prolific in the world',
      'Russian players are particularly known for their strong analytical approaches to the game',
      "Many of the world's top online poker players by volume and winnings are based in Russia",
    ],
    cities: [
      { name: 'Moscow', slug: 'moscow' },
      { name: 'Saint Petersburg', slug: 'saint-petersburg' },
      { name: 'Novosibirsk', slug: 'novosibirsk' },
      { name: 'Yekaterinburg', slug: 'yekaterinburg' },
      { name: 'Kazan', slug: 'kazan' },
    ],
  },
  {
    name: 'Ukraine',
    slug: 'ukraine',
    region: 'Europe',
    summary:
      "Ukraine has a passionate and talented poker community, with Kyiv's casino scene producing strong players who are well-represented on the international circuit. Ukrainian players have built a reputation for solid fundamentals and competitive intensity at every level of the game.",
    keyFacts: [
      'Ukrainian players have cashed extensively at World Series of Poker and European Poker Tour events',
      'Kyiv hosts active casino poker rooms that attract a competitive player base',
      'Ukraine has produced a number of high-profile online poker professionals',
      'The Ukrainian poker community is known for producing technically strong, disciplined players',
      'Online poker is widely played in Ukraine, with players active on major international platforms',
    ],
    cities: [
      { name: 'Kyiv', slug: 'kyiv' },
      { name: 'Kharkiv', slug: 'kharkiv' },
      { name: 'Odesa', slug: 'odesa' },
      { name: 'Dnipro', slug: 'dnipro' },
      { name: 'Lviv', slug: 'lviv' },
    ],
  },
  {
    name: 'Hungary',
    slug: 'hungary',
    region: 'Europe',
    summary:
      "Hungary has a vibrant poker culture anchored by Budapest, which is one of Central Europe's most active live poker cities. Hungarian players are regulars at international events, and the country's casino scene has grown to support a large and enthusiastic player base.",
    keyFacts: [
      'Budapest is home to multiple active poker rooms and hosts international tournament stops',
      'Hungarian players regularly compete at major European and world poker events',
      'The Esztergom area just across from Bratislava is home to a major Central European poker venue',
      'Online poker has a substantial following among Hungarian recreational and competitive players',
      "Hungary's central location makes it a natural hub for Eastern European poker tourism",
    ],
    cities: [
      { name: 'Budapest', slug: 'budapest' },
      { name: 'Debrecen', slug: 'debrecen' },
      { name: 'Miskolc', slug: 'miskolc' },
      { name: 'Pécs', slug: 'pecs' },
      { name: 'Győr', slug: 'gyor' },
    ],
  },

  // ── Americas ─────────────────────────────────────────────────────────────────
  {
    name: 'United States',
    slug: 'united-states',
    region: 'Americas',
    summary:
      "The United States is home to the world's most iconic poker culture, from the neon-lit rooms of Las Vegas to the underground home games that launched countless careers. Birthplace of the World Series of Poker and Texas Hold'em as a cultural phenomenon, the US has more dedicated poker players, live venues, and tournament action than any other country on the planet.",
    keyFacts: [
      'Las Vegas is the global capital of live poker, hosting the World Series of Poker every summer',
      'The US has produced more world champion poker players than any other country by a wide margin',
      'Millions of Americans play poker regularly, spanning every skill level from casual home games to high-stakes professionals',
      'Major poker tours including the WSOP, WPT, and numerous regional circuits run events across the country year-round',
      'Several US states have launched fully regulated online poker markets, with more expected to follow',
    ],
    cities: [],
  },
  {
    name: 'Canada',
    slug: 'canada',
    region: 'Americas',
    summary:
      "Canada has one of the most passionate poker communities in the world, with major cities from Toronto to Vancouver hosting thriving live scenes and a large online player base. Canadian players have won World Series of Poker Main Events and are regularly counted among the world's best.",
    keyFacts: [
      'Canadian players have won the WSOP Main Event multiple times and countless other major titles',
      'Toronto, Vancouver, and Montreal all have active casino poker rooms and dedicated poker clubs',
      'Canada has produced a disproportionately high number of world-class professional poker players',
      'Online poker is enormously popular across the country, played by millions of Canadians',
      'The Canadian poker community is known for producing exceptionally well-rounded and strategic players',
    ],
    cities: [
      { name: 'Toronto', slug: 'toronto' },
      { name: 'Vancouver', slug: 'vancouver' },
      { name: 'Montreal', slug: 'montreal' },
      { name: 'Calgary', slug: 'calgary' },
      { name: 'Ottawa', slug: 'ottawa' },
    ],
  },
  {
    name: 'Mexico',
    slug: 'mexico',
    region: 'Americas',
    summary:
      'Mexico has a growing and enthusiastic poker community, with major cities like Mexico City, Monterrey, and Guadalajara building active live scenes. Mexican players are increasingly visible at North American and world tournaments, and online poker continues to gain popularity across the country.',
    keyFacts: [
      'Mexican players are growing presences at WSOP and World Poker Tour events',
      'Mexico City and Monterrey have active casino poker rooms drawing competitive fields',
      'Cancún and other resort destinations attract international poker tourism',
      'Online poker is played widely across Mexico and continues to grow year over year',
      "Mexico's young and tech-savvy population is rapidly embracing competitive online poker",
    ],
    cities: [
      { name: 'Mexico City', slug: 'mexico-city' },
      { name: 'Guadalajara', slug: 'guadalajara' },
      { name: 'Monterrey', slug: 'monterrey' },
      { name: 'Tijuana', slug: 'tijuana' },
      { name: 'Cancún', slug: 'cancun' },
    ],
  },
  {
    name: 'Brazil',
    slug: 'brazil',
    region: 'Americas',
    summary:
      'Brazil has one of the largest and most passionate poker communities in Latin America, fueled by a culture that embraces competition, strategy, and social play. Brazilian players have made major inroads on the international tournament scene and are among the most enthusiastic online players in the Southern Hemisphere.',
    keyFacts: [
      'Brazilian players have won WSOP bracelets and made final tables at major international tournaments',
      'São Paulo and Rio de Janeiro have active live poker communities with regular tournaments',
      'Brazil has one of the largest online poker player bases in South America',
      'Brazilian players are known for creative and aggressive styles of play',
      'The country hosts international poker events that draw players from across South America',
    ],
    cities: [
      { name: 'São Paulo', slug: 'sao-paulo' },
      { name: 'Rio de Janeiro', slug: 'rio-de-janeiro' },
      { name: 'Brasília', slug: 'brasilia' },
      { name: 'Salvador', slug: 'salvador' },
      { name: 'Fortaleza', slug: 'fortaleza' },
    ],
  },
  {
    name: 'Argentina',
    slug: 'argentina',
    region: 'Americas',
    summary:
      'Argentina has a sophisticated card-playing culture and a dedicated poker community that is active both live and online. Buenos Aires is a cosmopolitan city with an active casino scene, and Argentine players are becoming increasingly competitive on the international tournament circuit.',
    keyFacts: [
      'Buenos Aires has a well-developed casino scene with active poker rooms',
      'Argentine players regularly compete at South American and international poker events',
      'Online poker is popular across the country, with a growing base of serious players',
      'Argentina hosts regional poker tournaments that attract fields from across South America',
      'The Buenos Aires poker scene blends European sophistication with Latin American passion',
    ],
    cities: [
      { name: 'Buenos Aires', slug: 'buenos-aires' },
      { name: 'Córdoba', slug: 'cordoba' },
      { name: 'Rosario', slug: 'rosario' },
      { name: 'Mendoza', slug: 'mendoza' },
      { name: 'Mar del Plata', slug: 'mar-del-plata' },
    ],
  },
  {
    name: 'Colombia',
    slug: 'colombia',
    region: 'Americas',
    summary:
      'Colombia has a passionate and fast-growing poker community, with Bogotá and Medellín building active live scenes and an online player base that is expanding rapidly. Colombian players are increasingly visible at Latin American and international tournaments.',
    keyFacts: [
      'Colombian players are regular participants at the Latin American Poker Tour and international events',
      'Bogotá and Medellín have growing casino poker communities with active tournament schedules',
      'Online poker is gaining significant traction among younger Colombian players',
      'Colombia has produced a number of successful online professionals who compete internationally',
      "The country's poker scene reflects a broader culture of strategic thinking and competition",
    ],
    cities: [
      { name: 'Bogotá', slug: 'bogota' },
      { name: 'Medellín', slug: 'medellin' },
      { name: 'Cali', slug: 'cali' },
      { name: 'Barranquilla', slug: 'barranquilla' },
      { name: 'Cartagena', slug: 'cartagena' },
    ],
  },
  {
    name: 'Chile',
    slug: 'chile',
    region: 'Americas',
    summary:
      "Chile has a well-organized poker community anchored by Santiago's casino circuit, and Chilean players are some of the most technically proficient in South America. The country hosts international stops on the Latin American poker circuit and has a growing base of online players.",
    keyFacts: [
      "Santiago's casino scene is one of the most developed in South America",
      'Chilean players regularly compete at Latin American Poker Tour and other regional events',
      'Online poker has a dedicated following among Chilean recreational and serious players',
      'Chile hosts major poker tournaments that draw regional fields from across the continent',
      'Chilean players are known for disciplined, fundamentals-based approaches to the game',
    ],
    cities: [
      { name: 'Santiago', slug: 'santiago' },
      { name: 'Valparaíso', slug: 'valparaiso' },
      { name: 'Concepción', slug: 'concepcion' },
      { name: 'Antofagasta', slug: 'antofagasta' },
      { name: 'Temuco', slug: 'temuco' },
    ],
  },
  {
    name: 'Peru',
    slug: 'peru',
    region: 'Americas',
    summary:
      "Peru has a growing poker scene centered on Lima, with a dedicated player base that has produced increasingly competitive results at regional and international tournaments. Online poker is gaining popularity, and Lima's casino circuit continues to expand.",
    keyFacts: [
      'Lima has an active casino poker scene with regular tournament action',
      'Peruvian players are growing presences at the Latin American Poker Tour and regional events',
      'Online poker is increasingly popular among younger Peruvian players',
      'Lima hosts regional tournament stops that draw players from across the Andean region',
      "Peru's poker community is known for its hospitality and passion for the game",
    ],
    cities: [
      { name: 'Lima', slug: 'lima' },
      { name: 'Arequipa', slug: 'arequipa' },
      { name: 'Trujillo', slug: 'trujillo' },
      { name: 'Cusco', slug: 'cusco' },
      { name: 'Chiclayo', slug: 'chiclayo' },
    ],
  },
  {
    name: 'Uruguay',
    slug: 'uruguay',
    region: 'Americas',
    summary:
      "Uruguay is a poker-friendly country with well-developed casino infrastructure and a community that punches above its weight on the regional circuit. Montevideo's casino scene and annual tournament events make it a hub for South American poker culture.",
    keyFacts: [
      'Montevideo has a well-established casino poker scene with professional-quality tournament action',
      'Uruguay hosts the Uruguayan Poker Open and other significant regional events',
      'Uruguayan players are respected competitors at Latin American and international events',
      'Online poker has a loyal following among Uruguayan recreational and serious players',
      "The country's small size belies a poker community that is impressively active and well-organized",
    ],
    cities: [
      { name: 'Montevideo', slug: 'montevideo' },
      { name: 'Salto', slug: 'salto' },
      { name: 'Paysandú', slug: 'paysandu' },
      { name: 'Maldonado', slug: 'maldonado' },
      { name: 'Ciudad de la Costa', slug: 'ciudad-de-la-costa' },
    ],
  },

  // ── Asia-Pacific ────────────────────────────────────────────────────────────
  {
    name: 'Australia',
    slug: 'australia',
    region: 'Asia-Pacific',
    summary:
      "Australia has one of the world's most vibrant poker communities, with a culture that has enthusiastically embraced the game at every level. From Sydney and Melbourne's bustling poker rooms to iconic Australian players who have conquered international stages, the country is a genuine global poker powerhouse.",
    keyFacts: [
      'Australian players have won multiple WSOP Main Events and major international titles',
      'The Aussie Millions in Melbourne is one of the most prestigious poker festivals in the world',
      'Sydney, Melbourne, Brisbane, and Perth all have thriving live poker room scenes',
      'Online poker is enormously popular in Australia and enjoyed by players across all states',
      'Australia has produced a disproportionately high number of world-class professional poker players',
    ],
    cities: [
      { name: 'Sydney', slug: 'sydney' },
      { name: 'Melbourne', slug: 'melbourne' },
      { name: 'Brisbane', slug: 'brisbane' },
      { name: 'Perth', slug: 'perth' },
      { name: 'Adelaide', slug: 'adelaide' },
    ],
  },
  {
    name: 'New Zealand',
    slug: 'new-zealand',
    region: 'Asia-Pacific',
    summary:
      "New Zealand has a small but active poker community that has produced players with impressive international credentials. The country's major cities host casino poker rooms, and online poker is broadly popular among New Zealand's tech-connected population.",
    keyFacts: [
      'New Zealand players regularly attend Australian events like the Aussie Millions and APT',
      'SkyCity casinos in Auckland and other cities host regular live poker tournaments',
      'Online poker is popular among New Zealand players competing on international platforms',
      "New Zealand's poker community is known for its friendly culture and strong sportsmanship",
      'Kiwi players have achieved notable results at international and regional poker events',
    ],
    cities: [
      { name: 'Auckland', slug: 'auckland' },
      { name: 'Wellington', slug: 'wellington' },
      { name: 'Christchurch', slug: 'christchurch' },
      { name: 'Hamilton', slug: 'hamilton' },
      { name: 'Tauranga', slug: 'tauranga' },
    ],
  },
  {
    name: 'Japan',
    slug: 'japan',
    region: 'Asia-Pacific',
    summary:
      "Japan has a quietly growing poker culture, with Tokyo's underground and legal poker venues attracting a dedicated community of players. Japanese players are increasingly visible at international tournaments, and the country's approach to strategy and discipline translates naturally to poker at the highest levels.",
    keyFacts: [
      'Tokyo has a growing number of legitimate poker venues and clubs with active player communities',
      'Japanese players have competed at World Series of Poker events and international stops',
      'The Asia Pacific Poker Tour has hosted events that brought significant international attention to Japan',
      'Online poker is played enthusiastically by a growing segment of Japanese players',
      "Japan's deep tradition of strategic games has helped fuel a methodical approach to poker",
    ],
    cities: [
      { name: 'Tokyo', slug: 'tokyo' },
      { name: 'Osaka', slug: 'osaka' },
      { name: 'Yokohama', slug: 'yokohama' },
      { name: 'Nagoya', slug: 'nagoya' },
      { name: 'Sapporo', slug: 'sapporo' },
    ],
  },
  {
    name: 'South Korea',
    slug: 'south-korea',
    region: 'Asia-Pacific',
    summary:
      'South Korea has a passionate poker community that has grown rapidly in recent years, fueled by widespread online gaming culture and a competitive national character. Korean players are increasingly competitive on the international circuit, and online poker is enormously popular.',
    keyFacts: [
      'South Korean players have made major inroads at international poker tournaments in recent years',
      "Seoul's poker community is active and growing, supported by a culture of competitive gaming",
      'Korean players are particularly well-represented in online poker high-stakes formats',
      'Kangwon Land casino near Seoul offers live poker in a spectacular mountain setting',
      "Online poker continues to grow in popularity among South Korea's large and connected player base",
    ],
    cities: [
      { name: 'Seoul', slug: 'seoul' },
      { name: 'Busan', slug: 'busan' },
      { name: 'Incheon', slug: 'incheon' },
      { name: 'Daegu', slug: 'daegu' },
      { name: 'Daejeon', slug: 'daejeon' },
    ],
  },
  {
    name: 'India',
    slug: 'india',
    region: 'Asia-Pacific',
    summary:
      "India has one of the fastest-growing poker communities in the world, driven by a large, young, English-speaking population with a passion for strategy and competition. Indian players have emerged on the global stage and the country's online poker market is booming.",
    keyFacts: [
      'India has produced several internationally successful poker players with major tournament results',
      'The Indian Poker Association and numerous domestic tours have formalized the competitive scene',
      'Online poker is one of the fastest-growing gaming sectors in India, played by millions',
      "Goa's casino boats offer live poker in one of Asia's most unique gaming environments",
      'Bangalore, Mumbai, and Hyderabad have active home game and club-level poker communities',
    ],
    cities: [
      { name: 'Mumbai', slug: 'mumbai' },
      { name: 'Delhi', slug: 'delhi' },
      { name: 'Bangalore', slug: 'bangalore' },
      { name: 'Chennai', slug: 'chennai' },
      { name: 'Kolkata', slug: 'kolkata' },
    ],
  },
  {
    name: 'Philippines',
    slug: 'philippines',
    region: 'Asia-Pacific',
    summary:
      'The Philippines is the poker capital of Southeast Asia, with Manila hosting some of the biggest tournaments in the Asia-Pacific region and a casino industry that is among the most developed in the world. Filipino players are enthusiastic, skilled, and increasingly prominent on the international stage.',
    keyFacts: [
      'Manila hosts the Poker King Club and major Asia-Pacific Poker Tour stops drawing global fields',
      "Resorts World Manila and Solaire Resort are among Asia's premier live poker venues",
      'Filipino players are widely regarded as some of the most skilled in the Asia-Pacific region',
      'The Philippines has a large and enthusiastic poker community across all skill levels',
      'Online poker is popular throughout the country, played by a wide and growing demographic',
    ],
    cities: [
      { name: 'Manila', slug: 'manila' },
      { name: 'Cebu City', slug: 'cebu-city' },
      { name: 'Davao', slug: 'davao' },
      { name: 'Quezon City', slug: 'quezon-city' },
      { name: 'Makati', slug: 'makati' },
    ],
  },
  {
    name: 'Thailand',
    slug: 'thailand',
    region: 'Asia-Pacific',
    summary:
      'Thailand has a passionate underground poker culture and a large number of expat players who contribute to an active international community. Bangkok and Phuket are well known among poker travelers, and Thai players are increasingly part of the broader Southeast Asian poker scene.',
    keyFacts: [
      "Thailand's major cities have active communities of both local and international poker players",
      'Bangkok is home to private poker clubs that attract players from across Southeast Asia',
      'Thai players are growing presences at regional Asia-Pacific poker tournaments',
      'Online poker is played widely by Thai players on international platforms',
      "Thailand is a popular destination for poker travelers combining play with the country's vibrant tourism offerings",
    ],
    cities: [
      { name: 'Bangkok', slug: 'bangkok' },
      { name: 'Chiang Mai', slug: 'chiang-mai' },
      { name: 'Phuket', slug: 'phuket' },
      { name: 'Pattaya', slug: 'pattaya' },
      { name: 'Hat Yai', slug: 'hat-yai' },
    ],
  },
  {
    name: 'Vietnam',
    slug: 'vietnam',
    region: 'Asia-Pacific',
    summary:
      "Vietnam has a rapidly growing poker community, with Ho Chi Minh City and Hanoi home to active clubs and a player base that is enthusiastic about online play. Vietnamese players are becoming more prominent at regional tournaments, and the country's gaming culture is expanding quickly.",
    keyFacts: [
      'Ho Chi Minh City and Hanoi have active poker club scenes with regular game action',
      'Vietnamese players are increasingly participating in Asia-Pacific poker tournaments',
      'Online poker is popular in Vietnam and continues to attract new players each year',
      "Vietnam's casino resorts, including those near Danang, offer live poker action",
      'The Vietnamese poker community is known for its energy and rapidly improving skill level',
    ],
    cities: [
      { name: 'Ho Chi Minh City', slug: 'ho-chi-minh-city' },
      { name: 'Hanoi', slug: 'hanoi' },
      { name: 'Da Nang', slug: 'da-nang' },
      { name: 'Hai Phong', slug: 'hai-phong' },
      { name: 'Can Tho', slug: 'can-tho' },
    ],
  },
  {
    name: 'Singapore',
    slug: 'singapore',
    region: 'Asia-Pacific',
    summary:
      "Singapore is a major hub for Asia-Pacific poker, home to two world-class integrated resorts that host some of the biggest live events in the region. The city-state's highly connected, international population makes it a natural center for competitive poker at the highest levels.",
    keyFacts: [
      'Marina Bay Sands and Resorts World Sentosa are world-renowned poker venues',
      'Singapore has hosted Asia-Pacific Poker Tour events drawing international fields',
      'Singaporean players are active competitors at major regional and international tournaments',
      "Online poker is played by a large portion of Singapore's connected, competitive population",
      "Singapore's status as a global financial hub attracts high-stakes players from around the world",
    ],
    cities: [
      { name: 'Marina Bay', slug: 'marina-bay' },
      { name: 'Orchard', slug: 'orchard' },
      { name: 'Sentosa', slug: 'sentosa' },
      { name: 'Jurong', slug: 'jurong' },
      { name: 'Tampines', slug: 'tampines' },
    ],
  },
  {
    name: 'Malaysia',
    slug: 'malaysia',
    region: 'Asia-Pacific',
    summary:
      "Malaysia has an active poker community driven largely by Kuala Lumpur's cosmopolitan population and the iconic Genting Highlands casino resort. Malaysian players are regulars at Asian poker circuit events, and online poker is popular among the country's large and connected middle class.",
    keyFacts: [
      "Genting Highlands near Kuala Lumpur is one of Asia's most iconic casino and poker destinations",
      'Malaysian players regularly participate in Asia-Pacific Poker Tour and regional circuit events',
      'Kuala Lumpur has an active poker community with club games and regular tournament action',
      "Online poker is popular throughout Malaysia's urban population",
      'Malaysia has hosted significant international and regional poker tournaments',
    ],
    cities: [
      { name: 'Kuala Lumpur', slug: 'kuala-lumpur' },
      { name: 'George Town', slug: 'george-town' },
      { name: 'Ipoh', slug: 'ipoh' },
      { name: 'Johor Bahru', slug: 'johor-bahru' },
      { name: 'Kota Kinabalu', slug: 'kota-kinabalu' },
    ],
  },
  {
    name: 'Indonesia',
    slug: 'indonesia',
    region: 'Asia-Pacific',
    summary:
      "Indonesia has a large and growing poker community despite the country's complex relationship with gaming. Online poker is played enthusiastically by millions of Indonesians, and the country's internationally connected players are increasingly visible at regional tournaments.",
    keyFacts: [
      'Indonesia has one of the largest populations in the world, with millions of online poker players',
      'Indonesian players are active participants in online poker on international platforms',
      "Jakarta's expat community contributes to a significant demand for live poker access",
      'Indonesian players are growing presences at Asia-Pacific poker events',
      "Bali attracts international poker tourists who combine play with the island's world-famous tourism",
    ],
    cities: [
      { name: 'Jakarta', slug: 'jakarta' },
      { name: 'Surabaya', slug: 'surabaya' },
      { name: 'Bandung', slug: 'bandung' },
      { name: 'Medan', slug: 'medan' },
      { name: 'Bali', slug: 'bali' },
    ],
  },
  {
    name: 'Taiwan',
    slug: 'taiwan',
    region: 'Asia-Pacific',
    summary:
      "Taiwan has a dedicated poker community that is well integrated into the broader Asia-Pacific gaming scene. Taipei's club poker environment and the country's competitive gaming culture have produced players who are increasingly prominent at regional events.",
    keyFacts: [
      'Taipei has an active poker club scene with regular tournaments and cash game action',
      'Taiwanese players regularly compete at Asia-Pacific poker circuit events',
      "Online poker is widely played among Taiwan's large and tech-savvy population",
      'Taiwan has produced a number of successful players on the international online circuit',
      "The country's gaming culture and competitive spirit translate naturally to poker",
    ],
    cities: [
      { name: 'Taipei', slug: 'taipei' },
      { name: 'Kaohsiung', slug: 'kaohsiung' },
      { name: 'Taichung', slug: 'taichung' },
      { name: 'Tainan', slug: 'tainan' },
      { name: 'Hsinchu', slug: 'hsinchu' },
    ],
  },
  {
    name: 'Hong Kong',
    slug: 'hong-kong',
    region: 'Asia-Pacific',
    summary:
      "Hong Kong's cosmopolitan, international character has made it a natural home for a vibrant poker community. The city's financial culture and love of competition have fueled a scene that draws players from across Asia, and Hong Kong residents are active participants in the region's biggest events.",
    keyFacts: [
      "Hong Kong's poker community is driven by its large international and finance-sector population",
      'HK players are regulars at Macau and Manila tournament stops as well as APPT events',
      "Online poker is popular among Hong Kong's highly connected and competitive player base",
      'The city has produced players who are prominent in both live and online high-stakes games',
      'Hong Kong serves as a gateway for Asia-Pacific poker activity, closely tied to the Macau scene',
    ],
    cities: [
      { name: 'Central', slug: 'central' },
      { name: 'Tsim Sha Tsui', slug: 'tsim-sha-tsui' },
      { name: 'Causeway Bay', slug: 'causeway-bay' },
      { name: 'Mong Kok', slug: 'mong-kok' },
      { name: 'Wan Chai', slug: 'wan-chai' },
    ],
  },
  {
    name: 'China',
    slug: 'china',
    region: 'Asia-Pacific',
    summary:
      "China has a massive and passionate poker community, with players from across the country competing internationally and contributing enormously to the global player pool. Chinese players have achieved major results on the world stage, and poker's popularity continues to grow across the country's vast population.",
    keyFacts: [
      'Chinese players have won major international poker titles and are regulars at WSOP and APPT events',
      'Shanghai, Beijing, and Shenzhen have large communities of serious poker players and enthusiasts',
      'Chinese players are a major force in online poker globally, with many competing at high stakes',
      "Poker's strategic nature resonates deeply with Chinese players and cultural traditions around games",
      'Major international poker events in Asia are significantly shaped by Chinese player participation',
    ],
    cities: [
      { name: 'Shanghai', slug: 'shanghai' },
      { name: 'Beijing', slug: 'beijing' },
      { name: 'Guangzhou', slug: 'guangzhou' },
      { name: 'Shenzhen', slug: 'shenzhen' },
      { name: 'Chengdu', slug: 'chengdu' },
    ],
  },

  // ── Middle East & Africa ────────────────────────────────────────────────────
  {
    name: 'South Africa',
    slug: 'south-africa',
    region: 'Africa',
    summary:
      "South Africa has the most developed poker community on the African continent, anchored by Johannesburg's casino circuit and a player base that is competitive, passionate, and increasingly connected to international events. The country's well-developed gaming infrastructure supports a thriving live and online poker scene.",
    keyFacts: [
      'Johannesburg and Cape Town have the most active live poker rooms and tournament scenes in Africa',
      'South African players have competed at World Series of Poker and other major international events',
      'The Montecasino and Suncoast Casino venues host regular major poker tournaments',
      'Online poker is growing rapidly in South Africa as internet access expands',
      'South Africa hosts the Poker Africa tournament series, attracting players from across the continent',
    ],
    cities: [
      { name: 'Johannesburg', slug: 'johannesburg' },
      { name: 'Cape Town', slug: 'cape-town' },
      { name: 'Durban', slug: 'durban' },
      { name: 'Pretoria', slug: 'pretoria' },
      { name: 'Port Elizabeth', slug: 'port-elizabeth' },
    ],
  },
  {
    name: 'Israel',
    slug: 'israel',
    region: 'Middle East',
    summary:
      "Israel has produced some remarkably successful international poker players from a relatively small player base, reflecting the country's deep culture of strategic thinking and competitive drive. Israeli players are regulars at major world events, and online poker has a loyal following within the country.",
    keyFacts: [
      'Israeli players have won WSOP bracelets and made deep runs at the biggest international tournaments',
      'The Israeli poker community is known for its analytical rigor and high skill level',
      'Tel Aviv has an active poker scene with dedicated clubs and home game networks',
      'Israeli players are particularly prominent in online poker at high and mid-stakes levels',
      'Israel has produced a number of well-respected professional players and coaches',
    ],
    cities: [
      { name: 'Tel Aviv', slug: 'tel-aviv' },
      { name: 'Jerusalem', slug: 'jerusalem' },
      { name: 'Haifa', slug: 'haifa' },
      { name: 'Beer Sheva', slug: 'beer-sheva' },
      { name: 'Rishon LeZion', slug: 'rishon-lezion' },
    ],
  },
  {
    name: 'Turkey',
    slug: 'turkey',
    region: 'Middle East',
    summary:
      "Turkey has a growing poker community, with Istanbul's large, cosmopolitan population forming the base of a scene that is increasingly connected to European poker culture. Turkish players are passionate about the game and online poker is widely enjoyed across the country.",
    keyFacts: [
      'Istanbul has a sizeable and enthusiastic poker community with active home game networks',
      'Turkish players are growing presences at European poker events',
      "Online poker is popular among Turkey's large and digitally connected population",
      "Turkey's unique position bridging Europe and Asia makes its poker scene culturally diverse",
      'The Turkish poker community continues to grow, producing increasingly competitive players',
    ],
    cities: [
      { name: 'Istanbul', slug: 'istanbul' },
      { name: 'Ankara', slug: 'ankara' },
      { name: 'Izmir', slug: 'izmir' },
      { name: 'Bursa', slug: 'bursa' },
      { name: 'Antalya', slug: 'antalya' },
    ],
  },
  {
    name: 'United Arab Emirates',
    slug: 'united-arab-emirates',
    region: 'Middle East',
    summary:
      "The UAE's cosmopolitan cities, particularly Dubai, have a surprisingly active poker community driven by the country's massive international population. Expats and visitors from around the world contribute to private poker games and a culture that embraces the game's strategic and social dimensions.",
    keyFacts: [
      "Dubai's large expat community sustains an active private poker scene",
      "The UAE's international character means poker players from dozens of countries play together regularly",
      'UAE-based players are active participants in online poker on international platforms',
      'Abu Dhabi and Dubai both have engaged poker communities within their expat populations',
      'The UAE serves as a travel hub, making it a meeting point for poker players from across the globe',
    ],
    cities: [
      { name: 'Dubai', slug: 'dubai' },
      { name: 'Abu Dhabi', slug: 'abu-dhabi' },
      { name: 'Sharjah', slug: 'sharjah' },
      { name: 'Ajman', slug: 'ajman' },
      { name: 'Ras Al Khaimah', slug: 'ras-al-khaimah' },
    ],
  },
]

export const regions = [
  'Europe',
  'Americas',
  'Asia-Pacific',
  'Africa',
  'Middle East',
] as const

export type Region = (typeof regions)[number]
